import type { Conversation } from '@/lib/api';

export interface MonthGroup {
  /** `AAAA-MM` no fuso de quem lê: é o cabeçalho e o `dateTime` do grupo. */
  month: string;
  conversations: Conversation[];
}

const DAY_MS = 86_400_000;

/** `AAAA-MM` da data no fuso local: uma conversa de 31/10 às 23h é de outubro aqui. */
export function monthKey(iso: string): string {
  const date = new Date(iso);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Agrupa por mês da última mensagem, na ordem em que a lista chega (a API já
 * entrega da mais recente para a mais antiga): o primeiro grupo é o mês mais
 * novo e dentro dele nada é reordenado.
 */
export function groupByMonth(conversations: readonly Conversation[]): MonthGroup[] {
  const groups: MonthGroup[] = [];
  for (const conversation of conversations) {
    const month = monthKey(conversation.lastMessageAt);
    const last = groups[groups.length - 1];
    if (last?.month === month) last.conversations.push(conversation);
    else groups.push({ month, conversations: [conversation] });
  }
  return groups;
}

const MONTH_FORMAT = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });

/** O subtítulo de um grupo `AAAA-MM`, como título: "Outubro de 2026". */
export function monthLabel(month: string): string {
  const date = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1);
  const label = MONTH_FORMAT.format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Dias de calendário entre as duas datas, no fuso local (não blocos de 24h). */
function calendarDaysBetween(from: Date, to: Date): number {
  const start = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const end = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((end - start) / DAY_MS);
}

/**
 * Data relativa curta do padrão da busca ("Hoje", "Ontem", "3 dias", "2 sem.",
 * "4 meses", "1 ano"). Uma data no futuro (relógio adiantado) conta como hoje.
 */
export function relativeDay(iso: string, now: Date = new Date()): string {
  const days = Math.max(0, calendarDaysBetween(new Date(iso), now));
  if (days === 0) return 'Hoje';
  if (days === 1) return 'Ontem';
  if (days < 7) return `${days} dias`;
  if (days < 30) return `${Math.floor(days / 7)} sem.`;
  if (days < 365) {
    const months = Math.floor(days / 30);
    return months === 1 ? '1 mês' : `${months} meses`;
  }
  const years = Math.floor(days / 365);
  return years === 1 ? '1 ano' : `${years} anos`;
}
